import React from "react"
import {v4 as uuidv4} from "uuid"
import {PassportDocumentForm} from "./Forms/PassportDocumentForm"
import {IdDocumentForm} from "./Forms/IdDocumentForm"
import {SSNDocumentForm} from "./Forms/SSNDocumentForm"
import {MatriculaConsularDocumentForm} from "./Forms/MatriculaConsularDocumentForm"
import {AddressVerificationDocumentForm} from "./Forms/AddressVerificationDocumentForm"
import {BusinessAddressVerificationDocumentForm} from "./Forms/BusinessAddressVerificationDocumentForm"
import {CertificateOfIncorporationDocumentForm} from "./Forms/CertificateOfIncorporationDocumentForm"
import {EmployerIdentificationNumberConfirmationDocumentForm} from "./Forms/EmployerIdentificationNumberConfirmationDocumentForm"
import {SelfieVerificationDocumentForm} from "./Forms/SelfieVerificationDocumentForm"
import {
	ApplicationDocument,
	ApplicationDocumentType,
	PatchApplicationDocument,
	PatchApplicationDocumentType,
} from "../../../resources/application"
import {
	AddressVerificationDocumentFormValues,
	BusinessAddressVerificationDocumentFormValues,
	CertificateOfIncorporationDocumentFormValues,
	EmployerIdentificationNumberConfirmationDocumentFormValues,
	IdDocumentFormValues,
	MatriculaConsularDocumentFormValues,
	PassportDocumentFormValues,
	SelfieVerificationDocumentFormValues,
	SSNDocumentFormValues,
} from "./Forms/types"

export const editDocumentFormId = "edit-document-form"

const errorComponent = <div>Unable to render Application Document Edit Page</div>

export interface EditDocumentFormProps {
	document: ApplicationDocument
	updateDocumentRequest: (document: PatchApplicationDocument) => void
}

const renderAddressVerificationForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	businessName: string | undefined,
	address: any,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (fullName && !!address) {
		return (
			<AddressVerificationDocumentForm
				documentType={documentType}
				initialValues={{
					address: address,
					fullName: fullName,
				}}
				onSubmit={(values: AddressVerificationDocumentFormValues) => {
					const updatedDocument = {
						type: PatchApplicationDocumentType.AddressVerification,
						attributes: Object.assign(values, idempotencyKey),
					}
					updateDocumentRequest(updatedDocument)
				}}
				formId={editDocumentFormId}
			/>
		)
	} else if (businessName && !!address) {
		return (
			<BusinessAddressVerificationDocumentForm
				documentType={documentType}
				initialValues={{
					address: address,
					businessName: businessName,
				}}
				onSubmit={(values: BusinessAddressVerificationDocumentFormValues) => {
					const updatedDocument = {
						type: PatchApplicationDocumentType.AddressVerification,
						attributes: Object.assign(values, idempotencyKey),
					}
					updateDocumentRequest(updatedDocument)
				}}
				formId={editDocumentFormId}
			/>
		)
	} else {
		return errorComponent
	}
}

const renderIdDocumentForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	dateOfBirth: Date,
	ssn: string,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!fullName) {
		return errorComponent
	}
	return (
		<IdDocumentForm
			documentType={documentType}
			initialValues={{
				fullName: fullName,
				dateOfBirth: dateOfBirth,
				ssn: ssn,
			}}
			onSubmit={(values: IdDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.IdDocument,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderSelfieVerificationForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!fullName) {
		return errorComponent
	}
	return (
		<SelfieVerificationDocumentForm
			documentType={documentType}
			initialValues={{
				fullName: fullName,
			}}
			onSubmit={(values: SelfieVerificationDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.SelfieVerification,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderPassportForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	dateOfBirth: Date,
	passport: string,
	nationality: string,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!fullName) {
		return errorComponent
	}
	return (
		<PassportDocumentForm
			documentType={documentType}
			initialValues={{
				fullName: fullName,
				dateOfBirth: dateOfBirth,
				passport: passport,
				nationality: nationality,
			}}
			onSubmit={(values: PassportDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.Passport,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderMatriculaConsularForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	dateOfBirth: Date,
	matriculaConsular: string,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!fullName) {
		return errorComponent
	}
	return (
		<MatriculaConsularDocumentForm
			documentType={documentType}
			initialValues={{
				fullName: fullName,
				dateOfBirth: dateOfBirth,
				matriculaConsularId: matriculaConsular,
			}}
			onSubmit={(values: MatriculaConsularDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.MatriculaConsular,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderSocialSecurityCardForm = (
	documentType: ApplicationDocumentType,
	fullName: any,
	dateOfBirth: Date,
	ssn: string,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!fullName) {
		return errorComponent
	}
	return (
		<SSNDocumentForm
			documentType={documentType}
			initialValues={{
				fullName: fullName,
				dateOfBirth: dateOfBirth,
				ssn: ssn,
			}}
			onSubmit={(values: SSNDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.SocialSecurityCard,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderCertificateOfIncorporationForm = (
	documentType: ApplicationDocumentType,
	businessName: string | undefined,
	stateOfIncorporation: string | undefined,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!stateOfIncorporation || !businessName) {
		return errorComponent
	}
	return (
		<CertificateOfIncorporationDocumentForm
			documentType={documentType}
			initialValues={{
				businessName: businessName,
				stateOfIncorporation: stateOfIncorporation,
			}}
			onSubmit={(values: CertificateOfIncorporationDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.CertificateOfIncorporation,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

const renderEmployerIdentificationNumberConfirmationForm = (
	documentType: ApplicationDocumentType,
	businessName: string | undefined,
	ein: string,
	updateDocumentRequest: (document: PatchApplicationDocument) => void,
	idempotencyKey: {idempotencyKey: string}
): JSX.Element => {
	if (!businessName) {
		return errorComponent
	}

	return (
		<EmployerIdentificationNumberConfirmationDocumentForm
			documentType={documentType}
			initialValues={{
				businessName: businessName,
				ein: ein,
			}}
			onSubmit={(values: EmployerIdentificationNumberConfirmationDocumentFormValues) => {
				const updatedDocument = {
					type: PatchApplicationDocumentType.EmployerIdentificationNumberConfirmation,
					attributes: Object.assign(values, idempotencyKey),
				}
				updateDocumentRequest(updatedDocument)
			}}
			formId={editDocumentFormId}
		/>
	)
}

export default function EditDocumentForm({document, updateDocumentRequest}: EditDocumentFormProps) {
	const {
		fullName,
		businessName,
		dateOfBirth,
		passport,
		ssn,
		ein,
		matriculaConsular,
		nationality,
		address,
		stateOfIncorporation,
	} = document.attributes
	const documentType = document.attributes.documentType
	const idempotencyKey = {idempotencyKey: `patch-document-dashboard-${uuidv4()}`}
	let documentComponent: JSX.Element

	switch (documentType) {
		case ApplicationDocumentType.addressVerification:
			documentComponent = renderAddressVerificationForm(
				documentType,
				fullName,
				businessName,
				address,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.idDocument:
			documentComponent = renderIdDocumentForm(
				documentType,
				fullName,
				dateOfBirth,
				ssn,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.selfieVerification:
			documentComponent = renderSelfieVerificationForm(documentType, fullName, updateDocumentRequest, idempotencyKey)
			break
		case ApplicationDocumentType.passport:
			documentComponent = renderPassportForm(
				documentType,
				fullName,
				dateOfBirth,
				passport,
				nationality,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.matriculaConsular:
			documentComponent = renderMatriculaConsularForm(
				documentType,
				fullName,
				dateOfBirth,
				matriculaConsular,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.socialSecurityCard:
			documentComponent = renderSocialSecurityCardForm(
				documentType,
				fullName,
				dateOfBirth,
				ssn,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.certificateOfIncorporation:
			documentComponent = renderCertificateOfIncorporationForm(
				documentType,
				businessName,
				stateOfIncorporation,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.employerIdentificationNumberConfirmation:
			documentComponent = renderEmployerIdentificationNumberConfirmationForm(
				documentType,
				businessName,
				ein,
				updateDocumentRequest,
				idempotencyKey
			)
			break
		case ApplicationDocumentType.powerOfAttorney:
			documentComponent = <div>Power Of Attorney Not Supported In Edit Mode</div>
			break
		default:
			documentComponent = <div>Unknown Document Type</div>
			break
	}

	return documentComponent
}
